// Native biometric (fingerprint / face) gate for the TWA.
// The hook is a no-op in the browser - there's no JS bridge available, so
// `isSupported` returns false and the consumer renders without the gate.

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'brainsync_biometric_enabled';
// Re-prompt after this idle period. 0 = every cold launch. Keep at 60s so
// quick app switches don't re-trigger.
const REPROMPT_AFTER_MS = 60 * 1000;
const LAST_UNLOCK_KEY = 'brainsync_biometric_last_unlock';

function readEnabled() {
    try {
        return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (_) {
        return false;
    }
}

function writeEnabled(on) {
    try {
        localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch (_) {}
}

function readLastUnlock() {
    try {
        return parseInt(localStorage.getItem(LAST_UNLOCK_KEY) || '0', 10);
    } catch (_) {
        return 0;
    }
}

function writeLastUnlock(ts) {
    try {
        localStorage.setItem(LAST_UNLOCK_KEY, String(ts));
    } catch (_) {}
}

/**
 * Hook returns:
 *   - isSupported: true on Android TWA with the BrainSyncBiometric bridge
 *   - canAuthenticate: integer status from Android (0 = ready, others = error)
 *   - enabled: user has opted in to biometric unlock
 *   - locked: app is currently gated (true on cold launch / resume after timeout)
 *   - unlock(): triggers the prompt; resolves with bool
 *   - setEnabled(on): toggles the opt-in (will prompt once to confirm enrollment)
 */
export function useBiometric() {
    const isSupported = typeof window !== 'undefined'
        && !!window.BrainSyncBiometric?.authenticate;

    const [enabled, setEnabledState] = useState(() => isSupported && readEnabled());
    const [canAuthenticate, setCanAuthenticate] = useState(0);
    const [locked, setLocked] = useState(false);

    // Sync canAuthenticate from the bridge.
    useEffect(() => {
        if (!isSupported) return;
        try {
            setCanAuthenticate(window.BrainSyncBiometric.canAuthenticate());
        } catch (_) {
            setCanAuthenticate(-1);
        }
    }, [isSupported]);

    // Compute initial lock state once the bridge has reported back.
    useEffect(() => {
        if (!isSupported || !enabled) {
            setLocked(false);
            return;
        }
        const last = readLastUnlock();
        const stale = !last || (Date.now() - last) > REPROMPT_AFTER_MS;
        setLocked(stale);
    }, [isSupported, enabled]);

    const unlock = useCallback(() => new Promise((resolve) => {
        if (!isSupported) {
            resolve(true);
            return;
        }
        const onResult = (event) => {
            window.removeEventListener('brainsync-biometric-result', onResult);
            const { ok } = event.detail || {};
            if (ok) {
                writeLastUnlock(Date.now());
                setLocked(false);
            }
            resolve(!!ok);
        };
        window.addEventListener('brainsync-biometric-result', onResult, { once: true });
        try {
            window.BrainSyncBiometric.authenticate(
                'Unlock BrainSync',
                'Use your fingerprint or face to continue',
                'Cancel'
            );
        } catch (_) {
            window.removeEventListener('brainsync-biometric-result', onResult);
            resolve(false);
        }
    }), [isSupported]);

    const setEnabled = useCallback(async (on) => {
        if (!isSupported) return false;
        if (on) {
            // Confirm the user can pass the prompt before flipping the flag -
            // otherwise we'd lock them out on next launch.
            const ok = await unlock();
            if (!ok) return false;
            writeEnabled(true);
            setEnabledState(true);
            return true;
        }
        // Disabling requires a successful biometric check too, so a thief
        // who picked up an unlocked phone can't disable the gate.
        const ok = await unlock();
        if (!ok) return false;
        writeEnabled(false);
        setEnabledState(false);
        setLocked(false);
        return true;
    }, [isSupported, unlock]);

    return { isSupported, canAuthenticate, enabled, locked, unlock, setEnabled };
}
